// The share table (solution sharing plan, stage 7, O21/O22): search, a checkbox per role, a header
// checkbox for the rows shown, and, for grant-all admins, the roles a rule excludes, greyed with the
// reason on hover (and behind an info button, for touch screens and keyboards).
import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { notOfferedReason } from "utils/sharingSwitches";
import { tableRows, filterRows, headerState, toggleHeader, selectionLine, shortList } from "utils/shareTable";

const cellSx = { fontSize: 13, py: 0.5 };
// Read by screen readers only; takes no space (inside the positioned scroll box).
const visuallyHidden = { position: "absolute", width: 1, height: 1, padding: 0, margin: -1, overflow: "hidden", clip: "rect(0 0 0 0)", whiteSpace: "nowrap", border: 0 };

function Administrators({ list }) {
  const s = shortList(list);
  if (!s.all.length) return <span>-</span>;
  return (
    <span>
      {s.shown.join(", ")}
      {s.more > 0 && (
        <Tooltip title={s.all.join(", ")} enterTouchDelay={0} disableInteractive>
          <Button size="small" sx={{ minWidth: 0, p: 0, ml: 0.5, fontSize: 12, textTransform: "none" }} aria-label={`All administrator roles: ${s.all.join(", ")}`}>
            {`+${s.more} more`}
          </Button>
        </Tooltip>
      )}
    </span>
  );
}

export default function ShareTable({ targets, notOffered, notOfferedCount, grantAll, selected, onSelect, maxHeight = 340 }) {
  const [search, setSearch] = useState("");
  const rows = useMemo(() => tableRows(targets, notOffered), [targets, notOffered]);
  const shownOffered = filterRows(rows.offered, search);
  const shownGreyed = filterRows(rows.greyed, search);
  const header = headerState(shownOffered, selected);
  const line = selectionLine(selected, shownOffered.map((r) => String(r._id)));
  const sel = new Set((selected || []).map(String));
  const toggle = (id) => onSelect(sel.has(id) ? selected.filter((x) => String(x) !== id) : [...selected, id]);
  const cols = grantAll ? 5 : 4;

  if (!rows.offered.length && !rows.greyed.length) {
    return (
      <Box>
        <Typography sx={{ fontSize: 14 }}>
          {grantAll ? "No role can be chosen: every role already has this solution, or cannot receive it." : "No role can receive this solution right now."}
        </Typography>
        {!grantAll && !notOfferedCount && <Typography sx={{ fontSize: 13, color: "#666", mt: 0.5 }}>Roles need Receive Shared Solutions switched on.</Typography>}
        {!grantAll && notOfferedCount > 0 && <Typography sx={{ fontSize: 13, color: "#666", mt: 0.5 }}>Some roles that accept shared solutions cannot receive this one.</Typography>}
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 14, mb: 1 }}>
        {!rows.offered.length
          ? "No role can be chosen: every role already has this solution, or cannot receive it."
          : grantAll
          ? "Every role that does not have it yet is listed."
          : "Only roles that accept shared solutions and do not have it yet are listed."}
      </Typography>
      <TextField size="small" fullWidth label="Search roles" value={search} onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }} inputProps={{ "aria-label": "Search roles" }} />
      <Box sx={{ position: "relative", maxHeight, overflowY: "auto", border: "1px solid #e0e0e0", borderRadius: 1 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox" sx={cellSx}>
                <Checkbox
                  size="small"
                  checked={header === "checked"}
                  indeterminate={header === "partial"}
                  disabled={header === "disabled"}
                  onChange={() => onSelect(toggleHeader(shownOffered, selected))}
                  inputProps={{ "aria-label": "Select all shown roles" }}
                />
              </TableCell>
              <TableCell sx={cellSx}>Role</TableCell>
              <TableCell sx={cellSx} align="right">Users</TableCell>
              <TableCell sx={cellSx}>Also given to (administrator roles)</TableCell>
              {grantAll && <TableCell sx={cellSx}>Accepts shared solutions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {shownOffered.map((t) => {
              const id = String(t._id);
              return (
                <TableRow key={id} hover data-testid={`share-row-${id}`}>
                  <TableCell padding="checkbox" sx={cellSx}>
                    <Checkbox size="small" checked={sel.has(id)} onChange={() => toggle(id)} inputProps={{ "aria-label": t.name }} />
                  </TableCell>
                  <TableCell sx={cellSx}>{t.name}</TableCell>
                  <TableCell sx={cellSx} align="right">{typeof t.users === "number" ? t.users : ""}</TableCell>
                  <TableCell sx={cellSx}><Administrators list={t.alsoTo} /></TableCell>
                  {grantAll && <TableCell sx={cellSx}>{t.receives === false ? "No" : "Yes"}</TableCell>}
                </TableRow>
              );
            })}
            {shownGreyed.length > 0 && (
              <TableRow>
                <TableCell colSpan={cols} sx={{ ...cellSx, fontWeight: 600, bgcolor: "#fafafa" }}>
                  Cannot be chosen directly (an administrator role among them may still receive a copy through another role you choose):
                </TableCell>
              </TableRow>
            )}
            {shownGreyed.map((r) => {
              const id = String(r._id);
              const reason = notOfferedReason(r.rule, r.detail);
              return (
                <Tooltip key={id} title={reason} placement="top-start" describeChild disableInteractive>
                  <TableRow data-testid={`share-row-greyed-${id}`} sx={{ "& td": { color: "#9e9e9e" } }}>
                    <TableCell padding="checkbox" sx={cellSx}>
                      <Checkbox size="small" disabled inputProps={{ "aria-label": r.name, "aria-describedby": `share-reason-${id}` }} />
                    </TableCell>
                    <TableCell sx={cellSx}>
                      {r.name}
                      <Tooltip title={reason} enterTouchDelay={0} disableInteractive>
                        <IconButton size="small" aria-label={`Why can't ${r.name} be chosen?`} sx={{ ml: 0.5, p: 0.25 }}>
                          <InfoOutlinedIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <span id={`share-reason-${id}`} style={visuallyHidden}>{reason}</span>
                    </TableCell>
                    <TableCell sx={cellSx} align="right">{typeof r.users === "number" ? r.users : ""}</TableCell>
                    <TableCell sx={cellSx}>-</TableCell>
                    {grantAll && <TableCell sx={cellSx}>{r.receives === false ? "No" : "Yes"}</TableCell>}
                  </TableRow>
                </Tooltip>
              );
            })}
            {!shownOffered.length && !shownGreyed.length && (
              <TableRow>
                <TableCell colSpan={cols} sx={cellSx}>No role matches the search.</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5, minHeight: 30 }}>
        <Typography sx={{ fontSize: 13 }} data-testid="share-selection-line">
          {`${line.n} selected`}
          {line.hidden > 0 && ` (${line.hidden} hidden by the search)`}
        </Typography>
        {line.n > 0 && (
          <Button size="small" onClick={() => onSelect([])} sx={{ textTransform: "none" }}>
            Clear selection
          </Button>
        )}
      </Box>
      {!grantAll && notOfferedCount > 0 && (
        <Typography sx={{ fontSize: 13, color: "#777" }}>Some roles that accept shared solutions cannot receive this one.</Typography>
      )}
    </Box>
  );
}
